// BulkAddDIDsDialog.jsx
//
// Admin-only dialog that lets an operator paste a free-form list of DIDs and
// add them all to one user under one carrier in a single workflow.
//
// Flow:
//   1. ENTRY        — textarea (any-delimiter list) + User + Carrier (+ optional Campaign).
//                     Apply → step 2.
//   2. CONFIRMATION — shows the parsed/deduped DID list and the shared fields.
//                     Confirm Add → step 3.
//   3. RESULTS      — fans out one POST /api/didresource per DID, in order,
//                     continuing past failures. Live progress, then summary of
//                     successes + per-DID failure reasons.
//
// No backend change — reuses the existing single-DID create endpoint.

import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import axios from "axios";
import { api } from "../../mockData";

// Any combination of comma, semicolon, whitespace (incl. newlines/tabs) is a
// valid delimiter. Empty fragments are dropped, leading/trailing whitespace
// is stripped. Order is preserved; duplicates are de-duplicated.
function parseDids(raw) {
  if (!raw) return [];
  const seen = new Set();
  const out = [];
  for (const tok of raw.split(/[\s,;|]+/)) {
    const v = tok.trim();
    if (!v) continue;
    if (seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

const STEP = { ENTRY: 0, CONFIRM: 1, RUNNING: 2, DONE: 3 };

export default function BulkAddDIDsDialog({
  open,
  onClose,
  users,
  carriers,
  campaigns,           // optional: list of { id, group_name } for the selected user
  onUserChange,        // fired when user selection changes (parent refetches campaigns)
  onCompleted,         // optional callback after the user closes a successful run
}) {
  const [step, setStep] = useState(STEP.ENTRY);
  const [rawText, setRawText] = useState("");
  const [userId, setUserId] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [campaignId, setCampaignId] = useState("");

  // run state
  const [results, setResults] = useState([]); // [{ did, ok, error }]
  const [runningIndex, setRunningIndex] = useState(0);

  const parsed = useMemo(() => parseDids(rawText), [rawText]);

  const reset = () => {
    setStep(STEP.ENTRY);
    setRawText("");
    setUserId("");
    setCarrierName("");
    setCampaignId("");
    setResults([]);
    setRunningIndex(0);
  };

  const handleClose = () => {
    // Block close while a run is in progress to avoid orphaned in-flight POSTs.
    if (step === STEP.RUNNING) return;
    if (step === STEP.DONE && typeof onCompleted === "function") {
      onCompleted({
        added: results.filter((r) => r.ok).length,
        failed: results.filter((r) => !r.ok).length,
      });
    }
    reset();
    onClose();
  };

  // Only require at least one parsed DID. User + Carrier are optional —
  // admin can paste a list now and assign user/carrier per-DID later.
  const canApply = parsed.length > 0;

  const startRun = async () => {
    setResults([]);
    setRunningIndex(0);
    setStep(STEP.RUNNING);

    const token = JSON.parse(localStorage.getItem("admin") || "null");
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token?.access_token}`,
      },
    };

    const collected = [];
    for (let i = 0; i < parsed.length; i++) {
      const did = parsed[i];
      setRunningIndex(i);
      const payload = {
        didnumber: did,
        did_status: "A",
        recording: true,
        description: "",
        ...(userId ? { user_id: userId } : {}),
        ...(carrierName ? { carrier_name: carrierName } : {}),
        ...(campaignId ? { redirect_group_id: campaignId } : {}),
      };
      try {
        const res = await axios.post(
          `${api.dev}/api/didresource`,
          JSON.stringify(payload),
          config,
        );
        const ok = res?.data?.status === 200;
        collected.push({
          did,
          ok,
          error: ok ? null : (res?.data?.message || "Unknown error"),
        });
      } catch (err) {
        collected.push({
          did,
          ok: false,
          error:
            err?.response?.data?.message ||
            err?.message ||
            "Request failed",
        });
      }
      // Update incrementally so the progress UI can show running counts.
      setResults([...collected]);
    }
    setRunningIndex(parsed.length);
    setStep(STEP.DONE);
  };

  const successCount = results.filter((r) => r.ok).length;
  const failureCount = results.filter((r) => !r.ok).length;
  const failures = results.filter((r) => !r.ok);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#092b5f",
          fontWeight: 700,
          fontSize: 18,
        }}
      >
        Bulk Add DIDs
        <IconButton size="small" onClick={handleClose} disabled={step === STEP.RUNNING}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ "& *": { color: "#212121 !important" } }}>
        {step === STEP.ENTRY && (
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: "#555 !important" }}>
              Paste DIDs separated by commas, semicolons, spaces, tabs, or new
              lines. Duplicates are removed automatically.
            </Typography>
            <TextField
              label="DIDs"
              multiline
              minRows={6}
              maxRows={14}
              fullWidth
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              placeholder={"18001234567, 18002345678\n18003456789 18004567890"}
              InputLabelProps={{ shrink: true }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                size="small"
                color={parsed.length ? "primary" : "default"}
                label={`${parsed.length} unique number${parsed.length === 1 ? "" : "s"} detected`}
              />
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="bulk-add-user-label">User (optional)</InputLabel>
                <Select
                  labelId="bulk-add-user-label"
                  label="User (optional)"
                  value={userId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setUserId(v);
                    setCampaignId("");
                    if (typeof onUserChange === "function") onUserChange(v);
                  }}
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {(users || []).map((u) => (
                    <MenuItem key={u.user_id} value={u.user_id}>
                      {u.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel id="bulk-add-carrier-label">Carrier (optional)</InputLabel>
                <Select
                  labelId="bulk-add-carrier-label"
                  label="Carrier (optional)"
                  value={carrierName}
                  onChange={(e) => setCarrierName(e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(carriers || []).map((c) => (
                    <MenuItem key={c.id || c.carrier_name} value={c.carrier_name}>
                      {c.carrier_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" disabled={!userId}>
                <Tooltip
                  title={!userId ? "Pick a user first to load their campaigns" : ""}
                  arrow
                  placement="top"
                >
                  <InputLabel id="bulk-add-campaign-label">Campaign (optional)</InputLabel>
                </Tooltip>
                <Select
                  labelId="bulk-add-campaign-label"
                  label="Campaign (optional)"
                  value={campaignId}
                  onChange={(e) => setCampaignId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(campaigns || []).map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.group_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Typography variant="caption" sx={{ color: "#777 !important" }}>
              Defaults applied to every DID: Status = Active, Recording = On.
              User and Carrier are optional — leave blank to add the DIDs
              now and assign per-DID later. Each DID is created via the
              standard create endpoint, so duplicate / format checks still apply.
            </Typography>
          </Stack>
        )}

        {step === STEP.CONFIRM && (
          <Stack spacing={2}>
            <Alert severity="info" sx={{ "& *": { color: "#0a3a6b !important" } }}>
              You are about to create <b>{parsed.length}</b> DID
              {parsed.length === 1 ? "" : "s"}
              {userId ? (
                <>
                  {" "}
                  for user{" "}
                  <b>
                    {(users || []).find((u) => String(u.user_id) === String(userId))?.username || userId}
                  </b>
                </>
              ) : (
                <> with <b>no user assigned</b> (can be set later)</>
              )}
              {carrierName ? (
                <> · carrier <b>{carrierName}</b></>
              ) : (
                <> · <b>no carrier</b> (can be set later)</>
              )}
              {campaignId ? (
                <>
                  {" "}
                  · campaign{" "}
                  <b>
                    {(campaigns || []).find((g) => String(g.id) === String(campaignId))?.group_name || campaignId}
                  </b>
                </>
              ) : null}
              . Status = Active, Recording = On.
            </Alert>
            <Box
              sx={{
                maxHeight: 320,
                overflowY: "auto",
                border: "1px solid #ddd",
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {parsed.map((d) => (
                  <Chip key={d} size="small" label={d} variant="outlined" />
                ))}
              </Stack>
            </Box>
          </Stack>
        )}

        {step === STEP.RUNNING && (
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: "#555 !important" }}>
              Adding {runningIndex + 1} of {parsed.length}…
            </Typography>
            <LinearProgress
              variant="determinate"
              value={parsed.length ? (runningIndex / parsed.length) * 100 : 0}
            />
            <Stack direction="row" spacing={2}>
              <Chip
                size="small"
                color="success"
                icon={<CheckCircleIcon />}
                label={`${results.filter((r) => r.ok).length} added`}
              />
              <Chip
                size="small"
                color="error"
                icon={<ErrorIcon />}
                label={`${results.filter((r) => !r.ok).length} failed`}
              />
            </Stack>
          </Stack>
        )}

        {step === STEP.DONE && (
          <Stack spacing={2}>
            <Alert
              severity={failureCount === 0 ? "success" : "warning"}
              sx={{ "& *": { color: failureCount === 0 ? "#1b5e20 !important" : "#7a4a00 !important" } }}
            >
              <b>{successCount}</b> DID{successCount === 1 ? "" : "s"} added,{" "}
              <b>{failureCount}</b> failed.
            </Alert>

            {failureCount > 0 && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1, color: "#7a1a1a !important" }}
                >
                  Failures
                </Typography>
                <Box
                  sx={{
                    maxHeight: 280,
                    overflowY: "auto",
                    border: "1px solid #f3c2c2",
                    borderRadius: 1,
                  }}
                >
                  {failures.map((r) => (
                    <Box
                      key={r.did}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                        px: 1.25,
                        py: 0.75,
                        borderBottom: "1px solid #f3c2c2",
                        "&:last-of-type": { borderBottom: "none" },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {r.did}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#7a1a1a !important", textAlign: "right" }}
                      >
                        {r.error}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === STEP.ENTRY && (
          <>
            <Button onClick={handleClose} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!canApply}
              onClick={() => setStep(STEP.CONFIRM)}
              sx={{
                textTransform: "none",
                color: "#fff !important",
                background:
                  "linear-gradient(180deg, #0E397F 0%, #001E50 100%) !important",
              }}
            >
              Apply
            </Button>
          </>
        )}

        {step === STEP.CONFIRM && (
          <>
            <Button onClick={() => setStep(STEP.ENTRY)} sx={{ textTransform: "none" }}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={startRun}
              sx={{
                textTransform: "none",
                color: "#fff !important",
                background:
                  "linear-gradient(180deg, #0E397F 0%, #001E50 100%) !important",
              }}
            >
              Confirm Add ({parsed.length})
            </Button>
          </>
        )}

        {step === STEP.RUNNING && (
          <Button disabled startIcon={<CircularProgress size={16} />} sx={{ textTransform: "none" }}>
            Working…
          </Button>
        )}

        {step === STEP.DONE && (
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              textTransform: "none",
              background:
                "linear-gradient(180deg, #0E397F 0%, #001E50 100%) !important",
            }}
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
